import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { ThemeProvider } from "@/shared/providers/ThemeProvider";
import { LanguageProvider } from "@/shared/providers/LanguageProvider";
import { AppPageLayout } from "@/modules/dashboard-shell";
import {
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  AccountTypeSelectPage,
  AdminDashboardPage,
  AddEmployeePage,
  EditEmployeePage,
  UsersPage,
  AddUserPage,
  EditUserPage,
  ProvidersPageRouter,
  AddProviderPage,
  EditProviderPage,
  BranchesPage,
  AddBranchPage,
  EditBranchPage,
  PackagesPage,
  PackageDetailPage,
  CreatePackagePage,
  EditPackagePage,
  ConventionsPage,
  ViewConventionPage,
  CreateConventionPage,
  EditConventionPage,
  BudgetConsumptionPage,
  AuthorizationRequestsPage,
  CreateAuthorizationRequestPage,
  EditAuthorizationRequestPage,
  DisputeDetailPage,
  CreateBackupPage,
  RestoreBackupPage,
  SystemLogsPage,
  CacheManagementPage,
  StatisticsPage,
  CreateDisputePage,
  AuditReportsPage,
  AuditReportDetailPage,
  SubmitAuditReportPage,
  GlobalInvoicePage,
  CompanyBillingPage,
  CreateCompanyBillingPage,
  EditCompanyBillingPage,
  HospitalExchangeRatesPage,
  PendingInvoicesPage,
  RejectedInvoicesPage,
  AuditedInvoicesPage,
  SubmitInvoicePage,
  CategoryValidationPage,
  HospitalDiagnosesPage,
  StandardActCodesPage,
  ActsOverviewPage,
  StandardDiagnosticCodesPage,
  AddStandardDiagnosticCodePage,
  AddStandardActCodePage,
  EditStandardActCodePage,
  EditStandardDiagnosticCodePage,
  PharmacyCatalogPage,
  HospitalPharmacyCatalogPage,
} from "@/modules/safecheck";
import { AdmissionsPageRouter } from "@/modules/safecheck/pages/AdmissionsPageRouter";
import { AuditorDashboardPage } from "@/modules/safecheck/pages/AuditorDashboardPage";
import { AllDisputesPage } from "@/modules/safecheck/pages/AllDisputesPage";
import { InProgressDisputesPage } from "@/modules/safecheck/pages/InProgressDisputesPage";
import { ProcessedDisputesPage } from "@/modules/safecheck/pages/ProcessedDisputesPage";
import { BeneficiariesPageRouter } from "@/modules/safecheck/pages/BeneficiariesPageRouter";
import { CreateAdmissionPage } from "@/modules/safecheck/pages/CreateAdmissionPage";
import { EmployeesPageRouter } from "@/modules/safecheck/pages/EmployeesPageRouter";
import { EnterpriseDashboardPage } from "@/modules/safecheck/pages/EnterpriseDashboardPage";
import { EnterpriseDashboardVisitsDetailPage } from "@/modules/safecheck/pages/EnterpriseDashboardVisitsDetailPage";
import { EnterpriseDashboardAgePyramidDetailPage } from "@/modules/safecheck/pages/EnterpriseDashboardAgePyramidDetailPage";
import { EnterpriseCompanyProfilePage } from "@/modules/safecheck/pages/EnterpriseCompanyProfilePage";
import { HospitalProviderProfilePage } from "@/modules/safecheck/pages/HospitalProviderProfilePage";
import { HospitalDashboardPage } from "@/modules/safecheck/pages/HospitalDashboardPage";
import { HospitalActsPageRouter } from "@/modules/safecheck/pages/HospitalActsPageRouter";
import {
  CompanyBillingDetailPageRouter,
  InvoiceDetailPageRouter,
} from "@/modules/safecheck/pages/InvoiceDetailPageRouter";
import { AdminSubmitInvoiceWizard } from "@/modules/safecheck/pages/AdminSubmitInvoiceWizard";
import { InvoicesPageRouter } from "@/modules/safecheck/pages/InvoicesPageRouter";
import { ApprovedInvoicesPageRouter } from "@/modules/safecheck/pages/ApprovedInvoicesPageRouter";
import { MessagingPageRouter } from "@/modules/safecheck/pages/MessagingPageRouter";
import { UserProfilePageRouter } from "@/modules/safecheck/pages/UserProfilePageRouter";
import { AnalyticsDashboard } from "@/modules/analytics-dashboard";
import { CrmDashboard } from "@/modules/crm-dashboard";
import { EcommerceDashboard } from "@/modules/ecommerce-dashboard";
import { LandingDashboard } from "@/modules/landing-dashboard";
import { AppStartupGate } from "@/modules/safecheck/components/AppStartupGate";
import { AuthProvider } from "@/modules/safecheck/providers/AuthProvider";
import {
  RedirectIfAuthenticated,
  RequireDashboardAccess,
} from "@/modules/safecheck/components/RequireDashboardAccess";

function DashboardLayout() {
  return (
    <AppPageLayout>
      <Outlet />
    </AppPageLayout>
  );
}

function AuditorDashboardRoute() {
  return <AuditorDashboardPage />;
}

function EditInvoiceRoute() {
  const { id } = useParams();
  const invoiceId = Number.parseInt(id ?? "", 10);
  return (
    <AdminSubmitInvoiceWizard
      invoiceId={Number.isNaN(invoiceId) || invoiceId <= 0 ? undefined : invoiceId}
    />
  );
}

function InvoiceDetailRoute() {
  const { id } = useParams();
  const displayId = Number.parseInt(id ?? "", 10);
  return <InvoiceDetailPageRouter displayId={Number.isNaN(displayId) ? 0 : displayId} />;
}

function CompanyBillingDetailRoute() {
  const { id } = useParams();
  const displayId = Number.parseInt(id ?? "", 10);
  return (
    <CompanyBillingDetailPageRouter displayId={Number.isNaN(displayId) ? 0 : displayId} />
  );
}

function EditStandardActCodeRoute() {
  const { id } = useParams();
  return <EditStandardActCodePage actId={id ?? ""} />;
}

function EditStandardDiagnosticCodeRoute() {
  const { id } = useParams();
  return <EditStandardDiagnosticCodePage diagnosticId={id ?? ""} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
        <AppStartupGate>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route element={<RedirectIfAuthenticated />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/forgot" element={<ForgotPasswordPage />} />
            <Route path="/login/reset" element={<ResetPasswordPage />} />
          </Route>
          <Route path="/select-account" element={<AccountTypeSelectPage />} />

          <Route path="/dashboard" element={<RequireDashboardAccess />}>
            <Route element={<DashboardLayout />}>
            <Route index element={<Navigate to="admin" replace />} />
            <Route path="admin" element={<AdminDashboardPage />} />
            <Route path="auditor" element={<AuditorDashboardRoute />} />
            <Route path="hospital" element={<HospitalDashboardPage />} />
            <Route path="enterprise" element={<EnterpriseDashboardPage />} />
            <Route path="enterprise/analytics/visits" element={<EnterpriseDashboardVisitsDetailPage />} />
            <Route path="enterprise/analytics/age-pyramid" element={<EnterpriseDashboardAgePyramidDetailPage />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="crm" element={<CrmDashboard />} />
            <Route path="ecommerce" element={<EcommerceDashboard />} />
            <Route path="landing" element={<LandingDashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/create" element={<AddUserPage />} />
            <Route path="users/:id/edit" element={<EditUserPage />} />
            <Route path="providers" element={<ProvidersPageRouter />} />
            <Route path="providers/create" element={<AddProviderPage />} />
            <Route path="providers/:id/edit" element={<EditProviderPage />} />
            <Route path="providers/:id" element={<ProvidersPageRouter />} />
            <Route path="employees" element={<EmployeesPageRouter />} />
            <Route path="employees/create" element={<AddEmployeePage />} />
            <Route path="employees/:id/edit" element={<EditEmployeePage />} />
            <Route path="employees/:id" element={<EmployeesPageRouter />} />
            <Route path="beneficiaries" element={<BeneficiariesPageRouter />} />
            <Route path="beneficiaries/:type/:id" element={<BeneficiariesPageRouter />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="branches/create" element={<AddBranchPage />} />
            <Route path="branches/:id/edit" element={<EditBranchPage />} />
            <Route path="acts" element={<ActsOverviewPage />} />
            <Route path="acts/by-hospital" element={<HospitalActsPageRouter />} />
            <Route path="pharmacy/catalog" element={<PharmacyCatalogPage />} />
            <Route path="pharmacy/products" element={<HospitalPharmacyCatalogPage />} />
            <Route path="acts/category-validation" element={<CategoryValidationPage />} />
            <Route path="acts/hospital-diagnoses" element={<HospitalDiagnosesPage />} />
            <Route path="acts/standard-codes" element={<StandardActCodesPage />} />
            <Route path="acts/standard-codes/create" element={<AddStandardActCodePage />} />
            <Route path="acts/standard-codes/:id/edit" element={<EditStandardActCodeRoute />} />
            <Route path="acts/standard-diagnostics" element={<StandardDiagnosticCodesPage />} />
            <Route path="acts/standard-diagnostics/create" element={<AddStandardDiagnosticCodePage />} />
            <Route path="acts/standard-diagnostics/:id/edit" element={<EditStandardDiagnosticCodeRoute />} />
            <Route path="admissions" element={<AdmissionsPageRouter />} />
            <Route path="admissions/:id" element={<AdmissionsPageRouter />} />
            <Route path="admissions/create" element={<CreateAdmissionPage />} />
            <Route path="invoices" element={<InvoicesPageRouter />} />
            <Route path="invoices/approved" element={<ApprovedInvoicesPageRouter />} />
            <Route path="invoices/generate" element={<GlobalInvoicePage />} />
            <Route path="company-billing" element={<CompanyBillingPage />} />
            <Route path="company-billing/create" element={<CreateCompanyBillingPage />} />
            <Route path="company-billing/:id/edit" element={<EditCompanyBillingPage />} />
            <Route path="billing/:id" element={<CompanyBillingDetailRoute />} />
            <Route path="exchange-rates" element={<HospitalExchangeRatesPage />} />
            <Route path="invoices/pending" element={<PendingInvoicesPage />} />
            <Route path="invoices/rejected" element={<RejectedInvoicesPage />} />
            <Route path="invoices/audited" element={<AuditedInvoicesPage />} />
            <Route path="invoices/submit" element={<SubmitInvoicePage />} />
            <Route path="invoices/:id/edit" element={<EditInvoiceRoute />} />
            <Route path="invoices/:id" element={<InvoiceDetailRoute />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="packages" element={<PackagesPage />} />
            <Route path="packages/create" element={<CreatePackagePage />} />
            <Route path="packages/:id" element={<PackageDetailPage />} />
            <Route path="packages/:id/edit" element={<EditPackagePage />} />
            <Route path="conventions" element={<ConventionsPage />} />
            <Route path="conventions/create" element={<CreateConventionPage />} />
            <Route path="conventions/:id" element={<ViewConventionPage />} />
            <Route path="conventions/:id/edit" element={<EditConventionPage />} />
            <Route path="budget-consumption" element={<BudgetConsumptionPage />} />
            <Route path="requests" element={<AuthorizationRequestsPage />} />
            <Route path="requests/create" element={<CreateAuthorizationRequestPage />} />
            <Route path="requests/:id/edit" element={<EditAuthorizationRequestPage />} />
            <Route path="backup" element={<Navigate to="/dashboard/backup/create" replace />} />
            <Route path="backup/create" element={<CreateBackupPage />} />
            <Route path="backup/restore" element={<RestoreBackupPage />} />
            <Route path="system-logs" element={<SystemLogsPage />} />
            <Route path="cache" element={<CacheManagementPage />} />
            <Route path="disputes" element={<AllDisputesPage />} />
            <Route path="disputes/create" element={<CreateDisputePage />} />
            <Route path="disputes/in-progress" element={<InProgressDisputesPage />} />
            <Route path="disputes/processed" element={<ProcessedDisputesPage />} />
            <Route path="disputes/:id" element={<DisputeDetailPage />} />
            <Route path="audit-reports" element={<AuditReportsPage />} />
            <Route path="audit-reports/submit" element={<SubmitAuditReportPage />} />
            <Route path="audit-reports/:id" element={<AuditReportDetailPage />} />
            <Route path="messaging" element={<MessagingPageRouter />} />
            <Route path="settings" element={<Navigate to="/dashboard/settings/user" replace />} />
            <Route path="settings/user" element={<UserProfilePageRouter />} />
            <Route path="settings/company" element={<EnterpriseCompanyProfilePage />} />
            <Route path="settings/hospital" element={<HospitalProviderProfilePage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </AppStartupGate>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
